import { UseQueryResult } from '@tanstack/react-query';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { getDomainStatusMessage } from '../../../extViewmodels/sdk/models/EDomainStatus';
import { IImpersonationContextComponentProps, IModalContext, ModalChildComponentContextKey } from '../../../models';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useEventLogging, withEventLogging } from '../../../models/Logging';
import { Token, UnsubscribeLinkPlaceholder } from '../../../models/Token';
import { createContentStateWithHtmlStringValue, createRichContentEditorStateWithText } from '../../../models/UiUtils';
import {
	useCheckDomainAvailability,
	useFeaturesMutation,
	useReleaseDomainMutation,
	useRequestNewHtmlNewsletterDomain,
} from '../../../queries';
import { AdminPermission } from '../../../viewmodels/AdminViewModels';
import {
	IAccountFeatures,
	IAddress,
	IHtmlNewsletterFeatures,
	IOperationResultNoValue,
	IRichContentEditorState,
	UnsubscribeTemplateViewModel,
} from '../../../viewmodels/AppViewModels';
import { brandPrimary, destructiveHover } from '../../styles/colors';
import { baseStyleSheet } from '../../styles/styles';
import { Button } from '../../components-v2/Button';
import { CanSpamLink } from '../CanSpamLink';
import { ConfirmationDialog, IConfirmationDialogOption } from '../ConfirmationDialog';
import { LoadingSpinner } from '../LoadingSpinner';
import { asModalComponent } from '../Modal';
import { InfoIcon } from '../svgs/icons/InfoIcon';
import { SuccessCheckIcon } from '../svgs/icons/SuccessCheckIcon';
import { WarningIconSmall, WarningIconType } from '../svgs/icons/WarningIcon';
import { XIcon } from '../svgs/icons/XIcon';
import { TextInput } from '../TextInput';
import { TinyPopover } from '../TinyPopover';
import { Toggle } from '../Toggle';
import { UnsubscribeSignature } from '../UnsubscribeSignature';
import { styleSheet } from './styles';

interface IProps extends IModalContext<IHtmlNewsletterFeatures>, IImpersonationContextComponentProps {
	address?: IAddress;
	className?: string;
	styles?: StyleDeclarationValue[];
	features?: IAccountFeatures | undefined;
	isFinancial?: boolean;
}

interface IState {
	unsubscribeContentEditorState?: IRichContentEditorState;
	domainNameAvailability: boolean | null;
	domainName: string;
	isDomainNameDisabled: boolean;
	infoIsOpen: boolean;
	useClientEmailProvider: boolean;
	showReleaseConfirm: boolean;
}

const _UnsubscribeSignatureHtmlModal = (props: IProps) => {
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const [state, setState] = React.useState<IState>({
		unsubscribeContentEditorState: undefined,
		domainNameAvailability: null,
		domainName: '',
		isDomainNameDisabled: false,
		infoIsOpen: false,
		useClientEmailProvider: false,
		showReleaseConfirm: false,
	});
	const domainNameRef = React.useRef<string>('');
	const mUnsubscribeTemplate = React.useRef(
		new UnsubscribeTemplateViewModel(userSession).impersonate(props.impersonationContext)
	);
	const { className, styles, parentModal, address, features, isFinancial } = props;

	const { unsubscribeContentEditorState } = state;
	const { logEvent, logApiError } = useEventLogging('UnsubscribeSignatureHtmlModal');
	const domainNameQuery: UseQueryResult<string | undefined, unknown> = useRequestNewHtmlNewsletterDomain({
		enabled: features?.htmlNewsletter?.domainName || state.useClientEmailProvider ? false : true,
		impersonationContext: mUnsubscribeTemplate.current.impersonationContext,
	});

	const featuresMutation = useFeaturesMutation({
		impersonationContext: mUnsubscribeTemplate.current.impersonationContext,
		onError: error => {
			logApiError('Features-Error', error);
			errorMessages.pushApiError(error);
		},
		onSuccess: data => {
			setState(prevState => ({
				...prevState,
				domainNameAvailability: null,
				isDomainNameDisabled: true,
				domainName: `${data.domainName}`,
			}));
			domainNameRef.current = `${data.domainName}`;
			parentModal?.onRequestClose(data, false);
		},
	});

	const checkAvailabilityMutation = useCheckDomainAvailability({
		impersonationContext: mUnsubscribeTemplate.current.impersonationContext,
		onError: error => {
			logApiError('CheckAvailability-Error', error);
			toast.error(error?.systemMessage);
			setState(prevState => ({
				...prevState,
				domainNameAvailability: false,
				isDomainNameDisabled: false,
			}));
		},
		onSuccess: data => {
			if (data) {
				setState(prevState => ({
					...prevState,
					domainNameAvailability: data,
					isDomainNameDisabled: true,
				}));
			}
		},
	});

	const releaseDomainMutation = useReleaseDomainMutation({
		impersonationContext: mUnsubscribeTemplate.current.impersonationContext,
		onSuccess: () => {
			toast.success('Domain released successfully');
			setState(prevState => ({
				...prevState,
				showReleaseConfirm: false,
			}));
			parentModal?.onRequestClose(null, false);
		},
		onError: error => {
			logApiError('ReleaseDomain-Error', error);
			toast.error(`Failed to release domain: ${error.systemMessage || 'Unknown error'}`);
			setState(prevState => ({
				...prevState,
				showReleaseConfirm: false,
			}));
		},
	});

	React.useEffect(() => {
		logEvent('LoadUnsubscribeTemplate');

		const promise = mUnsubscribeTemplate.current.loadHtmlNewsletterTemplate();
		if (promise) {
			promise
				?.then(() => {
					const template = mUnsubscribeTemplate?.current.htmlNewsletterTemplate;
					const contentHtmlStringValue = template?.content?.document;
					if (contentHtmlStringValue) {
						setState(prevState => ({
							...prevState,
							unsubscribeContentEditorState: createContentStateWithHtmlStringValue(
								contentHtmlStringValue.replace(/<\/?lev-content>/gim, '')
							),
						}));
					}
				})
				?.catch(error => {
					logApiError('LoadUnsubscribeTemplate-Error', error);
					errorMessages.pushApiError(error);
				});
		} else {
			setState(prevState => ({
				...prevState,
				unsubscribeContentEditorState: getDefaultContentEditorState(),
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [address]);

	React.useEffect(() => {
		if (features?.htmlNewsletter?.domainName) {
			setState(prevState => ({
				...prevState,
				domainName: `${features?.htmlNewsletter?.domainName}`,
				isDomainNameDisabled: true,
			}));
		}
	}, [features?.htmlNewsletter?.domainName]);

	React.useEffect(() => {
		setState(prevState => ({
			...prevState,
			useClientEmailProvider: features?.htmlNewsletter?.useClientEmailProvider ?? isFinancial ?? false,
		}));
	}, [features?.htmlNewsletter?.useClientEmailProvider, isFinancial]);

	React.useEffect(() => {
		if (domainNameQuery?.data && typeof domainNameQuery?.data === 'string') {
			setState(prevState => ({
				...prevState,
				domainName: `${domainNameQuery.data}`,
				isDomainNameDisabled: true,
			}));
			domainNameRef.current = domainNameQuery.data;
		}
	}, [domainNameQuery.data]);

	React.useEffect(() => {
		const errors = domainNameQuery.error as IOperationResultNoValue;
		if (errors?.systemMessage && domainNameQuery.isError) {
			toast.error(`${errors?.systemMessage}`);
			setState(prevState => ({
				...prevState,
				domainName: '',
				isDomainNameDisabled: false,
			}));
		}
	}, [domainNameQuery.error, domainNameQuery.isError]);

	const onContentEditorStateChanged = (_unsubscribeContentEditorState: IRichContentEditorState) => {
		setState(prevState => ({ ...prevState, unsubscribeContentEditorState: _unsubscribeContentEditorState }));
	};

	const onSaveUnsubscribeSignatureClicked = () => {
		if (!unsubscribeContentEditorState) {
			return;
		}
		const content = unsubscribeContentEditorState.getRawRichTextContent();
		logEvent('SaveUnsubscribeTemplate');
		mUnsubscribeTemplate.current
			.saveHtmlNewsletterTemplate(content)
			?.then(() => {
				const _features: IHtmlNewsletterFeatures = {
					...features?.htmlNewsletter,
					enabled: true,
					domainName: state.domainName,
					unsubscribeTemplateId: mUnsubscribeTemplate?.current.htmlNewsletterTemplate?.id,
				};

				_features.useClientEmailProvider = state.useClientEmailProvider;

				featuresMutation.mutate({
					features: _features,
				});
			})
			?.catch(error => {
				logApiError('SaveUnsubscribeTemplate-Error', error);
				errorMessages.pushApiError(error);
			});
	};

	const handleDomainNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.persist();
		setState(prevState => ({
			...prevState,
			domainName: e?.target?.value.split(' ').join('-'),
			domainNameAvailability: null,
		}));
	};

	const handleCheckAvailability = () => {
		checkAvailabilityMutation.mutate({
			domainName: state.domainName,
		});
	};

	const handleReset = () => {
		setState(prevState => ({
			...prevState,
			domainName: domainNameRef.current,
			domainNameAvailability: null,
		}));
	};

	const handleUseClientEmailProviderToggle = (useClientEmailProvider: boolean) => {
		setState(prevState => ({
			...prevState,
			useClientEmailProvider,
		}));
	};

	const onReleaseClicked = () => {
		setState(prevState => ({
			...prevState,
			showReleaseConfirm: true,
		}));
	};

	const handleReleaseConfirm = (_: void, cancel: boolean) => {
		if (cancel) {
			setState(prevState => ({
				...prevState,
				showReleaseConfirm: false,
			}));
			return;
		}

		if (!releaseDomainMutation.isPending) {
			releaseDomainMutation.mutate();
		}
	};

	const releaseDomainConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
		{
			isDestructive: true,
			title: 'Release Domain',
			disabled: releaseDomainMutation.isPending,
		},
		{
			isCancel: true,
			title: 'Cancel',
		},
	];

	const hasSupportPermission = userSession.hasPermission(AdminPermission.SupportLevel3);
	const isDomainStatusReleasable =
		features?.htmlNewsletter?.domainStatus === 'EmailValidationComplete' ||
		features?.htmlNewsletter?.domainStatus === 'EmailValidationFailed';
	const shouldShowReleaseButton =
		hasSupportPermission &&
		features?.htmlNewsletter?.domainName &&
		!state.useClientEmailProvider &&
		isDomainStatusReleasable;

	const getDefaultContentEditorState = () => {
		let defaultMessage = '';
		if (address) {
			defaultMessage += `${address.address1 ? address.address1 + ' ' : ''}${address.address2 ?? null}\n`;
			defaultMessage += `${address.city ? address.city + ', ' : ''}${
				address.stateProvince ? address.stateProvince + ' ' : ''
			}${address.postalCode || ''}\n`;
			defaultMessage += address.country ? address.country + '\n' : '';
		}
		const token = new Token(UnsubscribeLinkPlaceholder);
		defaultMessage += `\nIf you would no longer like to receive email from us, ${token.toHTMLString()} to unsubscribe`;
		return createRichContentEditorStateWithText(defaultMessage);
	};

	const getDomainStatusColor = (status: string) => {
		switch (status) {
			case 'RegistrationFailed':
			case 'EmailValidationFailed':
				return styleSheet.domainStatusFailed;
			case 'RegistrationPendingLevitateSupport':
			case 'RegistrationInProgress':
			case 'EmailValidationInProgress':
				return styleSheet.domainStatusInProgress;
			case 'EmailValidationComplete':
				return styleSheet.domainStatusSuccessful;
			case 'RegistrationComplete':
			case 'Released':
			case 'Unknown':
			default:
				return styleSheet.domainStatusFailed;
		}
	};

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} unsubscribe-signature-modal ${className || ''}`}>
			<div>HTML Newsletter Unsubscribe Signature</div>
			<div className={css(styleSheet.promptText)}>
				<div className={css(styleSheet.canspam)}>
					To comply with the
					<CanSpamLink styles={[styleSheet.link]} />
					this signature must contain:
				</div>
				<ul>
					<li>- The customer&apos;s physical address.</li>
					<li>
						{
							'- The unsubscribe link token. It will display as "click here" and can be inserted by typing {{unsubscribe link}}'
						}
					</li>
				</ul>
			</div>
			<UnsubscribeSignature
				contentEditorState={unsubscribeContentEditorState}
				editorStyles={[styleSheet.editor]}
				hideInstructions={true}
				onContentEditorStateChanged={onContentEditorStateChanged}
				isSetup={true}
			/>
			<div className={css(styleSheet.toggleWrap)}>
				<Toggle
					id='useClientEmailProviderToggle'
					isOn={state.useClientEmailProvider}
					text='HTML Sends Use Client Provider'
					onToggleCheckChanged={handleUseClientEmailProviderToggle}
					textStyles={[styleSheet.toggleText]}
					uncheckedColor='#ccc'
				/>
			</div>
			{!state.useClientEmailProvider ? (
				<>
					{domainNameQuery.isPending && domainNameQuery.isFetching ? (
						<div className={css(styleSheet.loaderWrap)}>
							<LoadingSpinner />
						</div>
					) : (
						<div className={css(styleSheet.domainNameWrap)}>
							<div className={css(styleSheet.domainNameInputWrap)}>
								<div>
									<label
										className={css(styleSheet.domainNameLabel)}
										htmlFor='domainName'
										onMouseEnter={() => setState(prevState => ({ ...prevState, infoIsOpen: true }))}
									>
										<span>Domain Name</span> <span className={css(styleSheet.errorRequired)}>*</span>
										{!features?.htmlNewsletter?.domainName ? (
											<div className={css(styleSheet.popoverWrap)}>
												<TinyPopover
													anchor={
														<figure className={css(styleSheet.infoIcon)}>
															<InfoIcon fillColor={brandPrimary} />
														</figure>
													}
													isOpen={state.infoIsOpen}
													dismissOnOutsideAction={true}
													onRequestClose={() => setState(prevState => ({ ...prevState, infoIsOpen: false }))}
													align='center'
													placement={['top', 'bottom', 'left', 'right']}
												>
													<p className={css(styleSheet.infoMessage)}>
														Domain name availability is not guaranteed. A domain name availability check is needed
														before the domain name can be assigned.
													</p>
												</TinyPopover>
											</div>
										) : null}
									</label>
								</div>
								<div className={css(styleSheet.inputWrap)}>
									<TextInput
										type='text'
										inputId='domainName'
										value={state.domainName}
										placeholder='example.com'
										className={`${css(styleSheet.input)} ${
											state.domainNameAvailability === false ? css(styleSheet.domainNameInputError) : ''
										}`}
										disabled={state.isDomainNameDisabled}
										onChange={handleDomainNameChange}
									/>
									{state.domainNameAvailability ? (
										<figure className={css(styleSheet.availabilityCheckIcon)}>
											<SuccessCheckIcon />
										</figure>
									) : null}
									{state.domainNameAvailability === false ? (
										<figure className={css(styleSheet.availabilityCheckIcon)}>
											<XIcon color={destructiveHover} height={12} width={12} className={css(styleSheet.xIcon)} />
										</figure>
									) : null}
								</div>
								{features?.htmlNewsletter?.domainName && features?.htmlNewsletter?.domainStatus && (
									<div className={css(styleSheet.domainStatusWrap)}>
										<div className={css(styleSheet.domainStatusLabel)}>Domain Status:</div>
										<div
											className={css(
												styleSheet.domainStatusMessage,
												getDomainStatusColor(features.htmlNewsletter.domainStatus)
											)}
										>
											{getDomainStatusMessage(features.htmlNewsletter.domainStatus)}
										</div>
									</div>
								)}
							</div>
							{!features?.htmlNewsletter?.domainName ? (
								<div className={css(styleSheet.availabilityCheckButtonWrap)}>
									{domainNameQuery?.data && state.isDomainNameDisabled ? (
										<button
											type='button'
											className={css(styleSheet.domainNameActionButton)}
											onClick={() =>
												setState(prevState => ({
													...prevState,
													isDomainNameDisabled: !state.isDomainNameDisabled,
												}))
											}
										>
											Edit
										</button>
									) : null}
									{(!state.isDomainNameDisabled && !(state.domainNameAvailability === false)) ||
									state.domainNameAvailability ? (
										<button
											type='button'
											disabled={domainNameQuery?.data === state.domainName}
											className={css(styleSheet.domainNameActionButton)}
											onClick={handleCheckAvailability}
										>
											{!checkAvailabilityMutation.isPending ? 'Check Availability' : <LoadingSpinner type='tiny' />}
										</button>
									) : null}
									{state.domainNameAvailability === false ? (
										<button
											type='button'
											disabled={domainNameQuery?.data === state.domainName}
											className={css(styleSheet.domainNameActionButton)}
											onClick={handleReset}
										>
											Reset
										</button>
									) : null}
								</div>
							) : null}
							{shouldShowReleaseButton ? (
								<div className={css(styleSheet.availabilityCheckButtonWrap)}>
									<Button variant='destructive' size='sm' onClick={onReleaseClicked}>
										Release Domain
									</Button>
								</div>
							) : null}
						</div>
					)}
				</>
			) : null}
			<button
				className={css(baseStyleSheet.ctaButton, styleSheet.button)}
				disabled={
					mUnsubscribeTemplate.current.isBusy ||
					((!state.domainName || state.domainNameAvailability === false) && !state.useClientEmailProvider) ||
					(Boolean(features?.htmlNewsletter?.domainName) &&
						!state.domainNameAvailability &&
						state.domainName !== features?.htmlNewsletter?.domainName)
				}
				onClick={onSaveUnsubscribeSignatureClicked}
			>
				{featuresMutation?.isPending ? <LoadingSpinner type='tiny' /> : 'Save'}
			</button>
			<ConfirmationDialog
				icon={<WarningIconSmall type={WarningIconType.Red} />}
				title='Release Domain?'
				options={releaseDomainConfirmationOptions}
				modalProps={{
					isOpen: state.showReleaseConfirm,
					onRequestClose: handleReleaseConfirm,
				}}
			>
				<div className={css(styleSheet.confirmDialogContent)}>
					<p>Are you sure you want to release {features?.htmlNewsletter?.domainName}?</p>
					<p>This will:</p>
					<ul>
						<li>Disable auto-renewal in AWS Route53</li>
						<li>Clear the domain from this account&apos;s HTML newsletter settings</li>
						<li>Record the release date and expiration date</li>
					</ul>
					<p>This action cannot be undone.</p>
				</div>
			</ConfirmationDialog>
		</div>
	);
};

const UnsubscribeSignatureHtmlModalAsObserver = observer(_UnsubscribeSignatureHtmlModal);
const UnsubscribeSignatureHtmlModalWithContext = inject(ModalChildComponentContextKey)(
	UnsubscribeSignatureHtmlModalAsObserver
);
const UnsubscribeSignatureHtmlModalWithEventLogging = withEventLogging(
	UnsubscribeSignatureHtmlModalWithContext,
	'UnsubscribeSignatureHtmlModal'
);
export const UnsubscribeSignatureHtmlModal = asModalComponent(UnsubscribeSignatureHtmlModalWithEventLogging, {
	useDefaultHeader: true,
});
